import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Calendar,
  Users,
  Award,
  Play,
  ChevronRight,
  GraduationCap,
  Clock,
  Star,
  TrendingUp,
  Check,
  Video,
  BarChart,
  Zap
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [institutionName, setInstitutionName] = useState('NPBC Theological College');
  const [institutionColors, setInstitutionColors] = useState(['#667eea', '#764ba2']);

  useEffect(() => {
    loadInstitutionData();
  }, []);

  const loadInstitutionData = () => {
    try {
      const selectedInstitutionString = localStorage.getItem('selectedInstitution');
      if (selectedInstitutionString) {
        const selectedInstitution = JSON.parse(selectedInstitutionString);
        setInstitutionName(selectedInstitution.name || 'NPBC Theological College');

        // Set colors for gradient
        if (selectedInstitution.primaryColor && selectedInstitution.secondaryColor) {
          setInstitutionColors([
            selectedInstitution.primaryColor,
            selectedInstitution.secondaryColor
          ]);
        }
      }
    } catch (error) {
      console.error('Error loading institution data:', error);
    }
  };

  // Featured Articles Data
  const featuredArticles = [
    {
      id: 1,
      title: "The Power of Faith in Biblical Studies",
      excerpt: "Exploring how faith deepens our understanding of Scripture and transforms theological education...",
      category: "Faith & Learning",
      readTime: "5 min read",
      image: "📖",
    },
    {
      id: 2,
      title: "Understanding Biblical Hermeneutics",
      excerpt: "A comprehensive guide to interpreting Scripture with historical and cultural context...",
      category: "Biblical Studies",
      readTime: "8 min read",
      image: "🔍",
    },
    {
      id: 3,
      title: "Church Leadership in Modern Times",
      excerpt: "Balancing traditional biblical principles with contemporary leadership challenges...",
      category: "Leadership",
      readTime: "6 min read",
      image: "👥",
    },
  ];

  // Quick Shortcuts Data
  const quickShortcuts = [
    { id: 1, title: "Daily Devotions", icon: "🙏", action: () => alert("Coming Soon: Daily devotions feature will be available soon.") },
    { id: 2, title: "Sermon Library", icon: "🎙️", action: () => alert("Coming Soon: Sermon library feature will be available soon.") },
    { id: 3, title: "Prayer Requests", icon: "⛪", action: () => alert("Coming Soon: Prayer requests feature will be available soon.") },
    { id: 4, title: "Bible Study", icon: "📚", action: () => navigate('/login') },
  ];

  // Latest News Data
  const latestNews = [
    {
      id: 1,
      title: "New Biblical Languages Course Launch",
      excerpt: "Exciting announcement: Hebrew and Greek language courses now available for all students seeking deeper scriptural understanding.",
      date: "3 days ago",
      category: "Academics",
    },
    {
      id: 2,
      title: "Campus Revival Week Highlights",
      excerpt: "Testimonies and insights from our recent campus-wide revival meetings that touched hearts across our community.",
      date: "1 week ago",
      category: "Campus Life",
    },
    {
      id: 3,
      title: "Theological Symposium 2025",
      excerpt: "Join renowned theologians and biblical scholars for our annual symposium on contemporary Christian thought.",
      date: "2 weeks ago",
      category: "Events",
    },
  ];

  const handleArticlePress = (article) => {
    alert(`Opening: ${article.title}`);
  };

  const handleNewsPress = (news) => {
    alert(`Opening: ${news.title}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Banner */}
      <div
        className="relative bg-gradient-to-r text-white py-16"
        style={{
          background: `linear-gradient(to right, ${institutionColors[0]}, ${institutionColors[1]})`
        }}
      >
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white/20 p-4 rounded-full">
              <GraduationCap className="w-12 h-12" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Welcome to {institutionName}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8">
            Faith • Learning • Community
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/login?role=student')}
              className="bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center"
            >
              Student Login
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
            <button
              onClick={() => navigate('/login?role=lecturer')}
              className="bg-white/20 text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors border border-white/30"
            >
              Lecturer Login
            </button>
          </div>
        </div>
      </div>

      {/* Free Trial Banner */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 py-4">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-white font-semibold text-lg">
            🎉 Start Your 30-Day Free Trial Today - Full Access to All Features!
          </p>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600">
            Choose the perfect plan for your institution
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Basic Package */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200 hover:border-blue-500 transition-all">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Basic</h3>
              <div className="flex items-baseline justify-center mb-4">
                <span className="text-4xl font-bold text-gray-900">KSh 500</span>
                <span className="text-gray-600 ml-2">/student/unit</span>
              </div>
              <p className="text-gray-600">Essential features for small institutions</p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Student enrollment management</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Course and unit management</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Assignment & quiz tools</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Grade tracking</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Learning materials upload</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Basic reporting</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Email notifications</span>
              </li>
            </ul>

            <button
              onClick={() => navigate('/institution/signup?package=basic')}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Start Free Trial
            </button>
          </div>

          {/* Pro Package */}
          <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl shadow-2xl p-8 text-white relative">
            <div className="absolute top-4 right-4 bg-yellow-400 text-purple-900 px-3 py-1 rounded-full text-sm font-bold">
              POPULAR
            </div>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold mb-2">Pro</h3>
              <div className="flex items-baseline justify-center mb-4">
                <span className="text-4xl font-bold">KSh 1,500</span>
                <span className="text-white/80 ml-2">/student/unit</span>
              </div>
              <p className="text-white/90">Advanced features with HD virtual classes</p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <Check className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <span className="font-medium">All Basic features</span>
              </li>
              <li className="flex items-start">
                <Video className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <span className="font-medium">HD Virtual Classroom</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <span>Live video lectures</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <span>Screen sharing & whiteboard</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <span>Session recording</span>
              </li>
              <li className="flex items-start">
                <BarChart className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <span>Advanced analytics dashboard</span>
              </li>
              <li className="flex items-start">
                <Zap className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <span>Priority support</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <span>Custom branding & API access</span>
              </li>
            </ul>

            <button
              onClick={() => navigate('/institution/signup?package=pro')}
              className="w-full bg-white text-purple-600 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Start Free Trial
            </button>
          </div>
        </div>

        <div className="text-center mt-8">
          <p className="text-gray-600">
            💡 Monthly billing based on enrolled students × units taken
          </p>
        </div>
      </div>

      {/* Featured Video Section */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
            <h2 className="text-2xl font-bold text-white mb-2">🎥 Featured Teaching</h2>
            <p className="text-blue-100">Latest biblical insights and teachings</p>
          </div>
          <div className="p-6">
            <div className="bg-gray-900 rounded-lg overflow-hidden">
              <div className="aspect-video bg-gray-800 flex items-center justify-center relative">
                <Play className="w-16 h-16 text-white" />
                <div className="absolute bottom-4 right-4 bg-black/70 text-white px-2 py-1 rounded text-sm">
                  25:30
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-white mb-1">
                  Understanding Biblical Prophecy
                </h3>
                <p className="text-gray-300 text-sm mb-2">
                  Dr. Sarah Mitchell • Systematic Theology
                </p>
                <div className="flex items-center space-x-4 text-sm text-gray-400">
                  <span>2.3K views</span>
                  <span>•</span>
                  <span>2 days ago</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Shortcuts */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">⚡ Quick Access</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickShortcuts.map((shortcut) => (
            <button
              key={shortcut.id}
              onClick={shortcut.action}
              className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow text-center"
            >
              <div className="text-3xl mb-3">{shortcut.icon}</div>
              <h3 className="font-semibold text-gray-900">{shortcut.title}</h3>
            </button>
          ))}
        </div>
      </div>

      {/* Featured Articles Slider */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">📚 Featured Articles</h2>
          <button className="text-blue-600 hover:text-blue-700 font-medium">
            View All
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {featuredArticles.map((article) => (
            <div
              key={article.id}
              onClick={() => handleArticlePress(article)}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow cursor-pointer overflow-hidden"
            >
              <div className="h-48 bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                <span className="text-6xl">{article.image}</span>
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                    {article.category}
                  </span>
                  <span className="text-gray-500 text-sm">{article.readTime}</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-gray-600 text-sm line-clamp-3">
                  {article.excerpt}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Verse of the Day */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8 text-center border border-blue-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">✨ Verse of the Day</h2>
          <blockquote className="text-lg text-gray-700 italic mb-4">
            "For I know the plans I have for you," declares the Lord, "plans to prosper you and not to harm you, to give you hope and a future."
          </blockquote>
          <cite className="text-blue-600 font-semibold">— Jeremiah 29:11 (NIV)</cite>
        </div>
      </div>

      {/* Latest News */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">📰 Latest News</h2>
          <button className="text-blue-600 hover:text-blue-700 font-medium">
            See All
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {latestNews.map((news) => (
            <div
              key={news.id}
              onClick={() => handleNewsPress(news)}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow cursor-pointer p-6"
            >
              <div className="flex justify-between items-start mb-3">
                <span className={`text-xs font-medium px-2 py-1 rounded ${news.category === 'Academics' ? 'bg-green-100 text-green-800' :
                  news.category === 'Campus Life' ? 'bg-blue-100 text-blue-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                  {news.category}
                </span>
                <span className="text-gray-500 text-sm">{news.date}</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {news.title}
              </h3>
              <p className="text-gray-600 text-sm line-clamp-3">
                {news.excerpt}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Community Stats */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">📊 Our Community</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-md p-6 text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">850+</div>
            <div className="text-gray-600">Students</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">45</div>
            <div className="text-gray-600">Faculty</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 text-center">
            <div className="text-3xl font-bold text-purple-600 mb-2">25+</div>
            <div className="text-gray-600">Courses</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 text-center">
            <div className="text-3xl font-bold text-orange-600 mb-2">12</div>
            <div className="text-gray-600">Campuses</div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Begin Your Spiritual Journey
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Join our community of learners growing in faith and knowledge
          </p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-flex items-center"
          >
            🎓 Go to Dashboard
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Landing;
